/**
 * Momentaufnahmen eines LiveKit-Raums: unveränderliche Ansichtsdaten für React (Kacheln) und der E2E-Hook
 * `window.__lfVoice` (Form siehe e2e/tests/helpers/voice.ts).
 */
import { Track, type Participant, type Room, type TrackPublication } from 'livekit-client';
import {
  parseParticipantMetadata,
  participantDisplayName,
  qualityLevel,
  sortByLocalThenName,
  type QualityLevel,
} from './logic';

export interface ParticipantView {
  /** LiveKit-Identität = userId. */
  userId: string;
  name: string;
  avatarUrl: string | null;
  isLocal: boolean;
  speaking: boolean;
  /** Mikrofon aus/stumm bzw. nicht veröffentlicht. */
  micMuted: boolean;
  deafened: boolean;
  hasVideo: boolean;
  hasScreen: boolean;
  /** Bildschirm mit Ton (Systemaudio) geteilt. */
  hasScreenAudio: boolean;
  quality: QualityLevel;
  cameraTrack: Track | null;
  screenTrack: Track | null;
}

function activePub(p: Participant, source: Track.Source): TrackPublication | undefined {
  const pub = p.getTrackPublication(source);
  return pub && !pub.isMuted ? pub : undefined;
}

export function participantView(p: Participant, isLocal: boolean, deafened: boolean): ParticipantView {
  const meta = parseParticipantMetadata(p.metadata);
  const camera = activePub(p, Track.Source.Camera);
  const screen = activePub(p, Track.Source.ScreenShare);
  return {
    userId: p.identity,
    name: participantDisplayName(p.identity, p.name, meta),
    avatarUrl: typeof meta?.avatarUrl === 'string' ? meta.avatarUrl : null,
    isLocal,
    speaking: p.isSpeaking,
    micMuted: !activePub(p, Track.Source.Microphone),
    deafened,
    hasVideo: !!camera,
    hasScreen: !!screen,
    hasScreenAudio: !!activePub(p, Track.Source.ScreenShareAudio),
    quality: qualityLevel(p.connectionQuality),
    cameraTrack: camera?.track ?? null,
    screenTrack: screen?.track ?? null,
  };
}

/** Alle Teilnehmer des Raums (eigene Kachel zuerst). `isDeafened` liefert den Taub-Status je userId. */
export function snapshotRoom(room: Room, isDeafened: (userId: string, isLocal: boolean) => boolean): ParticipantView[] {
  const local = room.localParticipant;
  const views = [participantView(local, true, isDeafened(local.identity, true))];
  for (const p of room.remoteParticipants.values()) views.push(participantView(p, false, isDeafened(p.identity, false)));
  return sortByLocalThenName(views);
}

export interface VoiceDebugTrack {
  source: string;
  kind: string;
  subscribed: boolean;
  muted: boolean;
}

export interface VoiceDebugParticipant {
  identity: string;
  isLocal: boolean;
  isSpeaking: boolean;
  tracks: VoiceDebugTrack[];
}

export interface VoiceDebugState {
  connected: boolean;
  status: string;
  channelId: string | null;
  participants: VoiceDebugParticipant[];
}

function debugParticipant(p: Participant, isLocal: boolean): VoiceDebugParticipant {
  const tracks: VoiceDebugTrack[] = [];
  for (const pub of p.trackPublications.values()) {
    tracks.push({
      source: pub.source,
      kind: pub.kind,
      // Eigene Tracks gelten als „abonniert", sobald sie existieren.
      subscribed: isLocal ? !!pub.track : pub.isSubscribed && !!pub.track,
      muted: pub.isMuted,
    });
  }
  return { identity: p.identity, isLocal, isSpeaking: p.isSpeaking, tracks };
}

/** Nur lesende Sicht für Playwright (`window.__lfVoice`). */
export function debugState(room: Room | null, channelId: string | null, status: string): VoiceDebugState {
  if (!room) return { connected: false, status, channelId: null, participants: [] };
  const participants = [debugParticipant(room.localParticipant, true)];
  for (const p of room.remoteParticipants.values()) participants.push(debugParticipant(p, false));
  return { connected: status === 'connected', status, channelId, participants };
}
